// CHG-1278 / ITER-3403 — v2→v1 hostname bridge (reverse of v1's
// lib/v2-host-bridge.ts).
//
// Per project_alora_dashboard_v1_v2_coexistence.md: v1 and v2 dashboards
// run permanently side-by-side at different hostnames. This module is
// the canonical hostname mapper consumed by ClassicDashboardLink in
// ProfileMenu.
//
// Mapping rules (mirror of toV2Hostname):
//   app.v2.aloragiving.com → app.aloragiving.com
//   app.v2.mohseen.app     → app.mohseen.app
//   app.v2.alorachurch.com → app.alorachurch.com
//   <any with v2.>         → strip "v2."
//
// localhost / IP / unrecognized → null (caller hides the link).

/**
 * Map a v2 hostname to its v1 equivalent. Returns null if the hostname
 * isn't a recognized `app.v2.*` pattern (e.g. localhost, staging-only
 * domains) — caller decides fallback.
 */
export function toV1Hostname(host: string): string | null {
  if (!host) return null;
  const bare = host.split(":")[0].trim().toLowerCase();
  if (
    bare === "localhost" ||
    /^\d+\.\d+\.\d+\.\d+$/.test(bare) ||
    !bare.includes(".")
  ) {
    return null;
  }
  // Already v1 → no-op (a curious user typed app.* into a v2 tab somehow)
  if (bare.startsWith("app.") && !bare.startsWith("app.v2.")) {
    return bare;
  }
  // app.v2.X.Y → app.X.Y
  if (bare.startsWith("app.v2.")) {
    return "app." + bare.slice("app.v2.".length);
  }
  return null;
}

/**
 * Build a fully-qualified https URL for the v1 (classic) dashboard. Path
 * intentionally NOT preserved: v1 and v2 have different route shapes;
 * landing on v1 root gives the user a known starting point.
 */
export function v1ClassicUrlForHostname(host: string): string | null {
  const v1Host = toV1Hostname(host);
  if (!v1Host) return null;
  return `https://${v1Host}/`;
}
